import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

// Shared
import Configs from '@/Configs';
import { fetchOptions, handleResponse } from '@/common/apiCalls';
import { queriesKeys } from '@/queries/queries-keys';
import type { QueryKey } from '@/types/queries';

export type PublicRecognitionRow = Record<string, unknown>;

export type GetPublicSingleRecognitionQueryParams = {
  recognitionID?: QueryKey;
  /** Optional; forwarded to `fetchOptions` when present. */
  correlationId?: string | null;
};

export type GetPublicSingleRecognitionQueryConfig = Omit<
  UseQueryOptions<PublicRecognitionRow | null, Error, PublicRecognitionRow | null>,
  'queryKey' | 'queryFn'
>;

const viteMeta = import.meta as unknown as {
  env?: Record<string, string | undefined>;
};

const UNAUTH_TOKEN = viteMeta.env?.VITE_UNAUTHENTICATED_REQUESTS_API_TOKEN;

/**
 * Public recognition by id (unauthenticated token from env).
 */
export function useGetPublicSingleRecognition(
  queryParams: GetPublicSingleRecognitionQueryParams = {},
  queryConfig: GetPublicSingleRecognitionQueryConfig = {}
) {
  const { recognitionID, correlationId = null } = queryParams;
  const { enabled = true, ...restQueryConfig } = queryConfig;

  return useQuery<PublicRecognitionRow | null, Error>({
    ...restQueryConfig,
    enabled: enabled && Boolean(recognitionID) && Boolean(UNAUTH_TOKEN),
    queryKey: queriesKeys.getPublicSingleRecognition(recognitionID),
    queryFn: async (): Promise<PublicRecognitionRow | null> => {
      const response = await fetch(
        Configs.platformGetPublicSingleRecognitionAPI + '?recognitionID=' + recognitionID,
        fetchOptions('GET', null, UNAUTH_TOKEN ?? null, 'json', correlationId)
      );
      const result = await handleResponse(response);
      if (typeof result === 'number') {
        throw new Error(`publicSingleRecognition:${result}`);
      }
      if (Array.isArray(result) && result.length > 0) {
        return result[0] as PublicRecognitionRow;
      }
      return null;
    },
  });
}
